import React, { useState, useRef, useEffect } from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePricingPlan } from '../../hooks/usePricingPlan';
import PlanBadge from './PlanBadge';

const ActivePricePlan: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDescription, setShowDescription] = useState(false);
  const { currentPlan } = usePricingPlan();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDescription = () => {
    setShowDescription(!showDescription);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDescription(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showDescription) {
        setShowDescription(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDescription]);

  return (
    <>
      {/* Plan Badge - Click to see description */}
      <div className="relative" ref={dropdownRef}>
        <button
          className="hover:scale-105 transition-all duration-200"
          onClick={toggleDescription}
        >
          <div className="md:hidden">
            <PlanBadge planId={currentPlan.id} name={currentPlan.name} size="sm" showText={false} />
          </div>
          <div className="hidden md:block">
            <PlanBadge planId={currentPlan.id} name={currentPlan.name} size="md" showText={true} />
          </div>
        </button>

        {/* Description Popup */}
        {showDescription && (
          <>
            {/* Mobile overlay */}
            <div className="fixed inset-0 bg-black bg-opacity-50 md:hidden" onClick={() => setShowDescription(false)} />
            
            {/* Mobile full-screen menu */}
            <div className="fixed inset-0 flex flex-col bg-card text-foreground z-50 md:hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-foreground">{t('subscription.current_plan', 'Current Plan')}</h3>
                <button
                  onClick={() => setShowDescription(false)}
                  className="p-2 rounded-lg hover:bg-muted transition-colors focus:outline-none text-foreground"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                <div className="mb-3">
                  <PlanBadge planId={currentPlan.id} name={currentPlan.name} size="sm" />
                </div>
                <p className="text-sm text-muted-foreground mb-3">{currentPlan.description}</p>
                <div className="space-y-2 mb-4">
                  {currentPlan.features.map((feature, index) => (
                    <div key={index} className="flex items-center text-sm text-foreground">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>
                {currentPlan.isUpgradeable && (
                  <button
                    onClick={() => navigate('/subscription')}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center"
                  >
                    {t('pricing.upgrade', 'Upgrade')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Desktop dropdown */}
            <div className="absolute right-0 top-full mt-2 w-80 bg-card text-foreground rounded-lg shadow-xl border border-gray-200 z-50 hidden md:block">
              <div className="p-4">
                <div className="mb-3">
                  <PlanBadge planId={currentPlan.id} name={currentPlan.name} size="sm" />
                </div>
                <p className="text-sm text-muted-foreground mb-3">{currentPlan.description}</p>
                <div className="space-y-2 mb-4">
                  {currentPlan.features.map((feature, index) => (
                    <div key={index} className="flex items-center text-sm text-foreground">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>
                {currentPlan.isUpgradeable && (
                  <button
                    onClick={() => navigate('/subscription')}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center justify-center"
                  >
                    {t('pricing.upgrade', 'Upgrade')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>


    </>
  );
};

export default ActivePricePlan;
